import { FC, useState, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import Loader from 'components/common/Loader';
import styles from './ConfirmDialog.module.css';
import Field from './Field';

type ConfirmDialogProps = {
  display: boolean;
  title: string;
  caption: string | string[];
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: (password?: string) => void;
  onCancel?: () => void;
  requirePassword?: boolean;
  requireTeamName?: string | null;
  errorCaption?: string | null;
  warningIcon?: boolean;
  isLoading?: boolean;
};

interface DialogInputs {
  teamName: string | null;
  password: string | null;
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  display,
  title,
  caption,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  warningIcon = false,
  requirePassword = false,
  requireTeamName = null,
  errorCaption = null,
  isLoading = false,
}) => {
  const [error, setError] = useState<string | null>(errorCaption);
  const [inputs, setInputs] = useState<DialogInputs>({
    password: null,
    teamName: null,
  });

  useEffect(() => {
    setError(errorCaption);
  }, [errorCaption]);

  const lockScroll = (lock: boolean) => {
    document.body.style.overflow = lock ? 'hidden' : 'auto';
  };

  useEffect(() => lockScroll(display), [display]);

  async function submitHandler(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (requireTeamName) {
      if (inputs.teamName !== requireTeamName) {
        return setError('Incorrect team name');
      }
    }
    setError(null);
    onConfirm(inputs.password ?? '');
  }

  return (
    <CSSTransition
      in={display}
      classNames={{
        enter: styles['DialogTransition-enter'],
        exit: styles['DialogTransition-exit'],
        enterActive: styles['DialogTransition-enter-active'],
        exitActive: styles['DialogTransition-exit-active'],
      }}
      timeout={200}
      unmountOnExit
    >
      <div className={styles.TransformContainer}>
        <div className={styles.DialogContainer}>
          <form className={styles.DialogBox} onSubmit={submitHandler}>
            <div className={styles.Mark}>
              <img alt="Rive" src="https://cdn.rive.app/icons/rive_mark.svg" />
            </div>
            <div className={styles.TitleContainer}>
              {warningIcon && <img src="/static/warning.png" alt="Alert" />}
              <h2>{title}</h2>
            </div>
            {Array.isArray(caption) ? (
              caption.map((captionItem, index) => (
                <p key={`dialog-caption-${index}`} className={styles.Caption}>
                  {captionItem}
                </p>
              ))
            ) : (
              <p className={styles.Caption}>{caption}</p>
            )}
            {(requireTeamName || requirePassword) && (
              <div className={styles.FieldContainer}>
                {requireTeamName && (
                  <Field
                    editable
                    fullWidth
                    autoFocus
                    label="Team name"
                    onChange={(e) => {
                      setInputs({
                        ...inputs,
                        teamName: e.target.value,
                      });
                    }}
                  />
                )}
                {requirePassword && (
                  <Field
                    editable
                    fullWidth
                    label="Password"
                    type="password"
                    onChange={(e) => {
                      setInputs({
                        ...inputs,
                        password: e.target.value,
                      });
                    }}
                  />
                )}
              </div>
            )}
            {error && <p className={styles.ErrorMessage}>{error}</p>}
            <div className={styles.ActionsContainer}>
              {isLoading ? (
                <Loader />
              ) : (
                <>
                  {cancelLabel && (
                    <button type="button" onClick={onCancel}>
                      {cancelLabel}
                    </button>
                  )}
                  <button type="submit">{confirmLabel}</button>
                </>
              )}
            </div>
          </form>
          <div className={styles.CancelContainer} onClick={onCancel} />
        </div>
      </div>
    </CSSTransition>
  );
};

export default ConfirmDialog;
